import React, { useEffect, useCallback, useState, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  FunnelIcon,
  ChevronLeftIcon,
} from '@heroicons/react/24/outline';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/app/rootReducer';
import {
  closeSearchModal,
  setSearchQuery,
  performSearch,
  fetchSuggestions,
  initializeSearch,
} from '../../slices/searchSlice';
import { useDebounce } from '../../hooks/useDebounce';
import SearchResults from '../SearchResults/SearchResults';
import SearchFilters from '../SearchFilters/SearchFilters';

const SearchModal: React.FC = () => {
  const dispatch = useDispatch();
  const {
    isModalOpen,
    isSearching,
    searchQuery,
    selectedFilters,
    error,
  } = useSelector((state: RootState) => state.search);

  const [localQuery, setLocalQuery] = useState(searchQuery);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const debouncedQuery = useDebounce(localQuery, 300);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus input when modal opens
  useEffect(() => {
    if (isModalOpen) {
      // Delay to ensure modal animation and Headless UI focus trap is complete
      const timer = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 300); // Increased delay for animation
      return () => clearTimeout(timer);
    }
  }, [isModalOpen]);

  // Initialize search service and load initial results when modal opens
  useEffect(() => {
    const loadInitialData = async () => {
      if (isModalOpen) {
        // Force refresh to get new secured key with correct filters
        await dispatch(initializeSearch(true));
        // Load all results initially (empty query shows everything) AFTER initialization
        await dispatch(
          performSearch({
            query: '',
            filters: selectedFilters,
            page: 0,
          })
        );
      }
    };
    loadInitialData();
  }, [isModalOpen, dispatch, selectedFilters]);

  // Perform search when debounced query or filters change
  useEffect(() => {
    // Only search if modal is open and initialized
    if (isModalOpen) {
      dispatch(
        performSearch({
          query: debouncedQuery,
          filters: selectedFilters,
          page: 0,
        })
      );
    }
  }, [debouncedQuery, selectedFilters, dispatch, isModalOpen]);

  // Fetch suggestions for autocomplete
  useEffect(() => {
    if (localQuery.trim() && localQuery.length >= 2) {
      dispatch(fetchSuggestions(localQuery));
    }
  }, [localQuery, dispatch]);

  const handleClose = useCallback(() => {
    dispatch(closeSearchModal());
    setLocalQuery('');
    setShowMobileFilters(false);
  }, [dispatch]);

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setLocalQuery(newQuery);
    dispatch(setSearchQuery(newQuery));
  };

  const handleRecentSearchClick = (search: string) => {
    // Update both local state and Redux state
    setLocalQuery(search);
    dispatch(setSearchQuery(search));
    // Close mobile filters when a recent search is selected
    setShowMobileFilters(false);
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isModalOpen) {
        if (showMobileFilters) {
          setShowMobileFilters(false);
        } else {
          handleClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen, showMobileFilters, handleClose]);

  // Count active filters for mobile badge
  const activeFilterCount = [
    selectedFilters.type !== 'all' ? 1 : 0,
    selectedFilters.resourceType ? 1 : 0,
    selectedFilters.eventType ? 1 : 0,
    selectedFilters.eventCategory ? 1 : 0,
    (selectedFilters.tags?.length || 0),
    (selectedFilters.categories?.length || 0),
  ].reduce((a, b) => a + b, 0);

  return (
    <Transition.Root show={isModalOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose} initialFocus={inputRef}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-2 sm:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all w-full max-w-6xl mt-2 sm:mt-8">
                {/* Search Header */}
                <div className="sticky top-0 z-20 border-b border-gray-200 bg-white">
                  <div className="px-3 py-3 sm:px-6 sm:py-4">
                    <div className="flex items-center">
                      <div className="flex-1 flex items-center">
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 mr-2 sm:mr-3 flex-shrink-0" />
                        <input
                          ref={inputRef}
                          type="text"
                          className="flex-1 border-0 bg-transparent placeholder-gray-500 focus:outline-none focus:ring-0 text-base sm:text-lg"
                          placeholder="Search events, resources..."
                          value={localQuery}
                          onChange={handleQueryChange}
                        />
                      </div>

                      {/* Mobile filter toggle */}
                      <button
                        type="button"
                        className="sm:hidden ml-2 p-2 rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none relative"
                        onClick={() => setShowMobileFilters(!showMobileFilters)}
                      >
                        <FunnelIcon className="h-5 w-5" aria-hidden="true" />
                        {activeFilterCount > 0 && (
                          <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-indigo-600 text-white text-xs flex items-center justify-center">
                            {activeFilterCount}
                          </span>
                        )}
                      </button>

                      <button
                        type="button"
                        className="ml-2 sm:ml-4 p-1 sm:p-0 rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                        onClick={handleClose}
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Search Body */}
                <div
                  className="flex relative"
                  style={{
                    height: 'calc(100vh - 120px)',
                    maxHeight: '600px'
                  }}
                >
                  {/* Desktop Filters Sidebar */}
                  <div className="hidden sm:block w-64 border-r border-gray-200 overflow-y-auto">
                    <SearchFilters onRecentSearchClick={handleRecentSearchClick} />
                  </div>

                  {/* Mobile Filters Panel */}
                  <Transition.Root show={showMobileFilters} as={Fragment}>
                    <div className="sm:hidden absolute inset-0 z-30">
                      <Transition.Child
                        as={Fragment}
                        enter="transition ease-in-out duration-300 transform"
                        enterFrom="-translate-x-full"
                        enterTo="translate-x-0"
                        leave="transition ease-in-out duration-300 transform"
                        leaveFrom="translate-x-0"
                        leaveTo="-translate-x-full"
                      >
                        <div className="relative h-full bg-white shadow-xl overflow-y-auto">
                          <div className="sticky top-0 z-10 flex items-center justify-between px-3 py-3 border-b border-gray-200 bg-white">
                            <h2 className="text-lg font-medium text-gray-900">Filters</h2>
                            <button
                              type="button"
                              className="p-1 rounded-md text-gray-400 hover:text-gray-500"
                              onClick={() => setShowMobileFilters(false)}
                            >
                              <ChevronLeftIcon className="h-5 w-5" />
                            </button>
                          </div>
                          <SearchFilters
                            onRecentSearchClick={handleRecentSearchClick}
                            isMobile={true}
                          />
                        </div>
                      </Transition.Child>
                    </div>
                  </Transition.Root>

                  {/* Results Area */}
                  <div className="flex-1 overflow-y-auto">
                    {error ? (
                      <div className="p-4 sm:p-6 text-center">
                        <p className="text-red-600 text-sm sm:text-base">Error: {error}</p>
                      </div>
                    ) : (
                      <SearchResults />
                    )}
                  </div>
                </div>

              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default SearchModal;